// The one place a human Label is written (docs/09 §4): owns the draft, the
// `POST /v1/answers` mutation (idempotent, `supersedes_id` on a diff), the
// review-feed continuation, and the concurrency notice. The results rail
// renders `editor` inside the expanded row and `question` (the marker-first
// / lazy-mint form behind `?label=new`) at its top, so the conversation's
// locator picks reach either draft.

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import { ApiRequestError, firstErrorMessage } from "../../lib/errors";
import { createIdempotencyKey } from "../../lib/idempotency";
import { queryKeys } from "../../lib/queryKeys";
import { reviewFeedFilters, type ReviewAnswer } from "../../lib/reviewFeed";
import { subscribeToAnswerCreated } from "../../lib/sse";
import type { ListQueryResult } from "../../lib/useListQuery";
import { useListQuery } from "../../lib/useListQuery";
import { useToast } from "../../ui";
import type { Assertion, Locator, MarkerOutput } from "../markerAuthoringDraft";
import {
  blankLabelDraft,
  buildLabelBody,
  charSpanLocator,
  draftFromAnswer,
  traceNodeLocator,
  utteranceRangeLocator,
  type InspectorLocalDraft,
  type LabelDraft,
} from "./labelInspectorDraft";
import { LabelInspector, NEW_QUESTION } from "./LabelInspector";
import { createHumanOnlyMarker } from "./createHumanOnlyMarker";
import { useReviewContinuation } from "./useReviewContinuation";
import {
  labelSeedKey,
  usePageToEnd,
  useRevisionPagination,
} from "./labelInspectorSupport";

type Answer = components["schemas"]["AnswerResponse"];
type Marker = components["schemas"]["MarkerResponse"];

// What the expanded results-rail row needs to render and save a Label.
export interface LabelEditor {
  answer: Answer | null;
  assertion: Assertion | null;
  draft: LabelDraft;
  history: Answer[];
  incomingAnswer?: Answer;
  isLoading: boolean;
  isSaving: boolean;
  loadError: string | null;
  onDismissIncoming: () => void;
  onDraftChange: (patch: Partial<LabelDraft>) => void;
  onNext: () => void;
  onPrevious: () => void;
  onRetry: () => void;
  onSave: () => void;
  onSaveNext: () => void;
  output: MarkerOutput;
  showNext: boolean;
  validationError: string | null;
}

export interface TranscriptLabeling {
  active: boolean;
  builtInMarkerIds: ReadonlySet<string>;
  editor: LabelEditor | null;
  feedBand?: ReactNode;
  hasMoreMarkers: boolean;
  isDirty?: boolean;
  isSaving?: boolean;
  locator?: Locator;
  onTextPick: (
    utteranceIndex: number,
    text: string,
    startCodeUnit: number,
    endCodeUnit: number,
  ) => void;
  onTraceNodePick: (nodeId: string) => void;
  onUtterancePick: (utteranceIndex: number) => void;
  question: ReactNode;
}

export interface UseLabelInspectorOptions {
  agentId?: string;
  answers: ListQueryResult<Answer>;
  feed?: string;
  label?: string;
  markerId?: string;
  markers: ListQueryResult<Marker>;
  onClose: () => void;
  onMarkerChange: (markerId: string | undefined) => void;
  onOpenNext: (answer: ReviewAnswer, afterSave?: boolean) => void;
  onReturnToReview?: () => void;
  // A Label saved without a feed advance: the caller decides what expands next.
  onSaved: (created: Answer) => void;
  transcriptId: string;
}

export function useLabelInspector(
  options: UseLabelInspectorOptions,
): TranscriptLabeling {
  const { agentId, answers, feed, label, markerId, markers, transcriptId } =
    options;
  const queryClient = useQueryClient();
  const toast = useToast();
  const isOpen = label !== undefined;

  usePageToEnd(isOpen || Boolean(feed), answers);
  // The rail needs every marker's assertion and built-in facts, and the
  // question form its full catalog, whatever page the list stopped on.
  usePageToEnd(agentId != null, markers);

  const listedAnswerCandidate =
    label === undefined || label === "new"
      ? undefined
      : answers.items.find((answer) => answer.id === label);
  const listedAnswer =
    listedAnswerCandidate?.transcript_id === transcriptId
      ? listedAnswerCandidate
      : undefined;
  const answerQuery = useQuery({
    enabled: isOpen && label !== "new" && listedAnswer == null,
    queryKey: queryKeys.answers.detail(label ?? "closed"),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/answers/{id}", {
        params: { path: { id: label ?? "" } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      if (data.transcript_id !== transcriptId) {
        throw new Error("This answer does not belong to this transcript.");
      }
      return data;
    },
  });
  const queriedAnswer =
    answerQuery.data?.transcript_id === transcriptId
      ? answerQuery.data
      : undefined;
  const answer = listedAnswer ?? queriedAnswer ?? null;
  const answerLoadError =
    answerQuery.data != null && queriedAnswer == null
      ? "This answer does not belong to this transcript."
      : answerQuery.isError
        ? (firstErrorMessage(answerQuery.error) ?? "Could not load Label.")
        : null;
  const seedKey = labelSeedKey(label, markerId, answer);
  const idempotency = useRef<{ key: string; signature: string } | null>(null);
  const mintedMarker = useRef<Marker | null>(null);
  const saving = useRef(false);
  const savedAnswer = useRef<{ seed: string; answer: Answer } | null>(null);
  const [savedDraft, setSavedDraft] = useState<string | null>(null);
  const [local, setLocal] = useState<InspectorLocalDraft>(() =>
    initialLocalDraft(seedKey, label, markerId, answer),
  );
  if (local.seedKey !== seedKey) {
    if (label !== "new" || markerId !== mintedMarker.current?.id) {
      mintedMarker.current = null;
    }
    const preserveNewQuestion =
      label === "new" && markerId == null && local.criterion === NEW_QUESTION;
    setLocal(
      preserveNewQuestion
        ? { ...local, seedKey }
        : initialLocalDraft(seedKey, label, markerId, answer),
    );
  }
  const selectedMarker =
    markers.items.find((marker) => marker.id === local.criterion) ??
    (mintedMarker.current?.id === local.criterion
      ? mintedMarker.current
      : undefined);
  const subjectMarkerId = answer?.marker_id ?? selectedMarker?.id;
  const revisions = useListQuery("/v1/markers/{id}/revisions", {
    enabled: answer != null,
    pathParams: { id: subjectMarkerId ?? "closed" },
    queryKey: queryKeys.markerRevisions.list({ marker_id: subjectMarkerId }),
  });
  useRevisionPagination(answer?.marker_revision_id, revisions);
  const pinnedRevision = revisions.items.find(
    (revision) => revision.id === answer?.marker_revision_id,
  );

  const output =
    answer?.marker_output ??
    selectedMarker?.current_revision.output ??
    local.newOutput;
  const assertion =
    answer != null
      ? (pinnedRevision?.assertion ?? null)
      : (selectedMarker?.current_revision.assertion ?? null);
  const history = useMemo(
    () =>
      answers.items
        .filter(
          (item) =>
            item.transcript_id === transcriptId &&
            item.marker_id === subjectMarkerId,
        )
        .sort(
          (left, right) =>
            Date.parse(right.created_at) - Date.parse(left.created_at),
        ),
    [answers.items, subjectMarkerId, transcriptId],
  );

  const me = useQuery({
    enabled: isOpen,
    queryKey: queryKeys.me,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/me");
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });
  const [incomingAnswerId, setIncomingAnswerId] = useState<string | null>(null);
  useEffect(
    () =>
      subscribeToAnswerCreated((payload) => {
        if (
          isOpen &&
          payload.transcript_id === transcriptId &&
          payload.source === "human" &&
          me.data?.user.id != null &&
          payload.author_id !== me.data.user.id
        ) {
          setIncomingAnswerId(payload.answer_id);
        }
      }),
    [isOpen, me.data?.user.id, transcriptId],
  );
  const incomingAnswer = answers.items.find(
    (item) => item.id === incomingAnswerId,
  );

  const draftSignature = JSON.stringify([
    seedKey,
    local.criterion,
    local.draft,
    local.newOutput,
    local.displayName,
  ]);
  const initial = initialLocalDraft(seedKey, label, markerId, answer);
  const initialSignature = JSON.stringify([
    seedKey,
    initial.criterion,
    initial.draft,
    initial.newOutput,
    initial.displayName,
  ]);
  const isDirty =
    isOpen &&
    draftSignature !== initialSignature &&
    draftSignature !== savedDraft;

  const save = useMutation({
    mutationFn: async (_advance: boolean) => {
      void _advance;
      setLocal((current) => ({ ...current, validationError: null }));
      if (label !== "new" && answer == null) {
        throw new Error(
          "The requested answer could not be loaded for this transcript.",
        );
      }
      if (answer) {
        await continuation.selection?.initialize(answer.id);
      }
      const marker =
        answer == null && local.criterion === NEW_QUESTION
          ? await createHumanOnlyMarker({
              agentId,
              output: local.newOutput,
              displayName: local.displayName,
            })
          : answer == null
            ? selectedMarker
            : null;
      if (marker != null && local.criterion === NEW_QUESTION) {
        mintedMarker.current = marker;
        queryClient.setQueryData(queryKeys.markers.detail(marker.id), marker);
        void queryClient.invalidateQueries({
          queryKey: queryKeys.markers.list(),
        });
        setLocal((current) => ({
          ...current,
          criterion: marker.id,
          validationError: null,
        }));
      }
      const resolvedMarkerId = answer?.marker_id ?? marker?.id;
      const markerRevisionId =
        answer?.marker_revision_id ?? marker?.current_revision.id;
      if (!resolvedMarkerId || !markerRevisionId) {
        throw new Error("Choose a Marker before saving.");
      }
      const body = buildLabelBody(
        local.draft,
        output,
        resolvedMarkerId,
        markerRevisionId,
        transcriptId,
        savedAnswer.current?.seed === seedKey
          ? savedAnswer.current.answer
          : answer,
      );
      if (typeof body === "string") {
        throw new Error(body);
      }
      const signature = JSON.stringify(body);
      if (idempotency.current?.signature !== signature) {
        idempotency.current = { key: createIdempotencyKey(), signature };
      }
      const { data, error } = await apiClient.POST("/v1/answers", {
        body,
        params: {
          header: { "Idempotency-Key": idempotency.current.key },
        },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: async (created, advance) => {
      setSavedDraft(draftSignature);
      savedAnswer.current = { seed: seedKey, answer: created };
      if (answer) {
        continuation.selection?.recordSave(answer.id, created.id);
      }
      idempotency.current = null;
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.answers.list({ transcript_id: transcriptId }),
        }),
        feed
          ? queryClient.invalidateQueries({
              queryKey: queryKeys.answers.list(reviewFeedFilters(feed)),
            })
          : Promise.resolve(),
      ]);
      toast.success("Label saved");
      if (advance && feed && label !== "new") {
        await continuation.move(1, true);
      } else {
        options.onSaved(created);
      }
    },
    onSettled: () => {
      saving.current = false;
    },
    onError: (error) => {
      setLocal((current) => ({
        ...current,
        validationError: firstErrorMessage(error) ?? "Could not save Label.",
      }));
    },
  });

  const continuation = useReviewContinuation({
    answer: isOpen ? answer : null,
    feed: isOpen && label !== "new" ? feed : undefined,
    isDirty,
    isSaving: save.isPending,
    onOpen: options.onOpenNext,
    onReturn: options.onReturnToReview,
  });

  function submit(advance: boolean): void {
    if (saving.current || continuation.isPending || continuation.ended) {
      return;
    }
    if (draftSignature === savedDraft) {
      if (advance) {
        if (continuation.selection) {
          void continuation.move(1, true);
        } else if (savedAnswer.current?.seed === seedKey) {
          options.onSaved(savedAnswer.current.answer);
        }
      }
      return;
    }
    saving.current = true;
    save.mutate(advance);
  }

  function updateDraft(patch: Partial<LabelDraft>): void {
    if (saving.current) {
      return;
    }
    setLocal((current) => ({
      ...current,
      draft: { ...current.draft, ...patch },
      utteranceAnchor:
        patch.locator != null && patch.locator.kind !== "utterance_range"
          ? null
          : current.utteranceAnchor,
      validationError: null,
    }));
  }

  const builtInMarkerIds = useMemo(
    () =>
      new Set(
        markers.items
          .filter((marker) => marker.builtin_key != null)
          .map((marker) => marker.id),
      ),
    [markers.items],
  );

  const showNext = continuation.selection != null;
  const editor: LabelEditor | null =
    isOpen && label !== "new" && !continuation.ended
      ? {
          answer,
          assertion,
          draft: local.draft,
          history,
          incomingAnswer,
          isLoading:
            (answer == null && answerQuery.isPending) ||
            markers.isPending ||
            continuation.isPending,
          isSaving: save.isPending,
          loadError: answerLoadError,
          onDismissIncoming: () => {
            setIncomingAnswerId(null);
          },
          onDraftChange: updateDraft,
          onNext: () => {
            setIncomingAnswerId(null);
            continuation.next();
          },
          onPrevious: continuation.previous,
          onRetry: () => {
            submit(save.variables ?? false);
          },
          onSave: () => {
            submit(false);
          },
          onSaveNext: () => {
            submit(true);
          },
          output,
          showNext,
          validationError: local.validationError,
        }
      : null;

  return {
    active: isOpen && !continuation.ended,
    builtInMarkerIds,
    editor,
    feedBand: continuation.band,
    hasMoreMarkers: markers.hasNextPage,
    isDirty: (isDirty || save.isPending) && !continuation.ended,
    isSaving: save.isPending,
    locator: isOpen ? local.draft.locator : undefined,
    onTextPick: (index, text, start, end) => {
      updateDraft({ locator: charSpanLocator(index, text, start, end) });
    },
    onTraceNodePick: (nodeId) => {
      updateDraft({ locator: traceNodeLocator(nodeId) });
    },
    onUtterancePick: (index) => {
      if (saving.current) {
        return;
      }
      setLocal((current) => ({
        ...current,
        draft: {
          ...current.draft,
          locator: utteranceRangeLocator(
            current.utteranceAnchor ?? index,
            index,
          ),
        },
        utteranceAnchor: current.utteranceAnchor == null ? index : null,
      }));
    },
    question:
      label === "new" ? (
        <LabelInspector
          assertion={assertion}
          isLoading={markers.isPending}
          isSaving={save.isPending}
          local={local}
          markers={selectableMarkers(markers.items, mintedMarker.current)}
          output={output}
          onClose={options.onClose}
          onCriterionChange={(criterion) => {
            if (saving.current) {
              return;
            }
            if (criterion === NEW_QUESTION) {
              options.onMarkerChange(undefined);
              setLocal((current) => ({
                ...current,
                criterion,
                draft: blankLabelDraft(),
                validationError: null,
              }));
            } else {
              options.onMarkerChange(criterion || undefined);
              setLocal((current) => ({ ...current, criterion }));
            }
          }}
          onDraftChange={updateDraft}
          onNewOutputChange={(newOutput) => {
            if (saving.current) {
              return;
            }
            setLocal((current) => ({
              ...current,
              draft: blankLabelDraft(),
              newOutput,
            }));
          }}
          onDisplayNameChange={(displayName) => {
            if (saving.current) {
              return;
            }
            setLocal((current) => ({ ...current, displayName }));
          }}
          onRetry={() => {
            submit(false);
          }}
          onSave={() => {
            submit(false);
          }}
        />
      ) : null,
  };
}

function selectableMarkers(
  markers: Marker[],
  mintedMarker: Marker | null,
): Marker[] {
  const available = markers.filter((marker) => marker.builtin_key == null);
  return mintedMarker == null ||
    available.some((marker) => marker.id === mintedMarker.id)
    ? available
    : [...available, mintedMarker];
}

function initialLocalDraft(
  seedKey: string,
  label: string | undefined,
  markerId: string | undefined,
  answer: Answer | null,
): InspectorLocalDraft {
  return {
    criterion: label === "new" ? (markerId ?? "") : (answer?.marker_id ?? ""),
    draft: answer ? draftFromAnswer(answer) : blankLabelDraft(),
    newOutput: "boolean",
    displayName: "",
    seedKey,
    utteranceAnchor: null,
    validationError: null,
  };
}
